import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Loader2, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.current_password || !formData.new_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (formData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword({
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      setSuccess(true);
      toast.success('Password changed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-0 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <CardContent className="pt-8 pb-8 text-center space-y-5">
            <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="font-heading text-xl font-bold">Password Changed</h2>
            <p className="text-sm text-muted-foreground">Your password has been updated successfully.</p>
            <Button
              onClick={() => navigate('/admin')}
              data-testid="back-to-dashboard-btn"
              className="w-full h-12 rounded-full font-semibold"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
        <div className="mt-6 text-center" data-testid="global-footer">
          <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-0 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="change-password-card">
        <CardHeader className="space-y-1 pb-4 text-center">
          <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound className="h-7 w-7 text-primary" />
          </div>
          <CardTitle className="font-heading text-xl sm:text-2xl">Change Password</CardTitle>
          <CardDescription className="text-sm">
            Update your password for {user?.email}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="current" className="text-sm font-medium">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="current"
                  type="password"
                  data-testid="current-password-input"
                  placeholder="Enter current password"
                  value={formData.current_password}
                  onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                  className="pl-10 h-12"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pw" className="text-sm font-medium">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-pw"
                  type="password"
                  data-testid="new-password-input"
                  placeholder="Min 6 characters"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  className="pl-10 h-12"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pw" className="text-sm font-medium">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-pw"
                  type="password"
                  data-testid="confirm-password-input"
                  placeholder="Re-enter new password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className="pl-10 h-12"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="submit-change-password-btn"
              className="w-full h-12 rounded-full font-semibold"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6 text-center" data-testid="global-footer">
        <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
        <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
      </div>
    </div>
  );
};

export default ChangePassword;
